"use strict";
/**
 * Stream-tree resolver helpers (Plan 45 §6).
 *
 * Walk the parent chain of a runtime Pryv stream tree looking for
 * `clientData.hdsCustomField[eventType]`.
 *
 * Inheritance semantics (Plan 45 §2.4):
 *   - present non-empty def → use it
 *   - present empty `{}` def → opt-out (return null)
 *   - missing → keep walking up
 *   - reached root with nothing → null
 *
 * Callers wanting to distinguish "no declaration" from "explicit opt-out" can use
 * `resolveStreamCustomFieldDetailed` which returns `{ kind, def? }`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildStreamMap = buildStreamMap;
exports.resolveStreamCustomFieldDetailed = resolveStreamCustomFieldDetailed;
exports.resolveStreamCustomField = resolveStreamCustomField;
exports.streamCustomFieldToVirtualItem = streamCustomFieldToVirtualItem;
exports.customFieldDeclarationToVirtualItem = customFieldDeclarationToVirtualItem;
const customFieldTypes_ts_1 = require("./customFieldTypes.js");
/** Build a flat id→stream map from a Pryv stream tree. */
function buildStreamMap(streamTree) {
    const map = new Map();
    function walk(nodes) {
        for (const s of nodes) {
            map.set(s.id, s);
            if (s.children?.length)
                walk(s.children);
        }
    }
    walk(streamTree);
    return map;
}
/**
 * Walk parent chain looking for `clientData.hdsCustomField[eventType]`.
 * Returns the resolved def, an opt-out marker, or `none`.
 *
 * `streamTreeOrMap` may be either a Pryv stream tree (`Pryv.Stream[]`) or a
 * pre-built id→stream map (cheaper when resolving many fields against the same tree).
 */
function resolveStreamCustomFieldDetailed(streamTreeOrMap, streamId, eventType) {
    const map = isStreamMap(streamTreeOrMap) ? streamTreeOrMap : buildStreamMap(streamTreeOrMap);
    const visited = new Set();
    let cur = map.get(streamId);
    while (cur) {
        if (visited.has(cur.id))
            return { kind: 'none' }; // defensive
        visited.add(cur.id);
        const cd = cur.clientData;
        const decl = cd?.hdsCustomField?.[eventType];
        if (decl !== undefined) {
            if ((0, customFieldTypes_ts_1.isEmptyDef)(decl))
                return { kind: 'optOut' };
            return { kind: 'def', def: decl };
        }
        const parentId = cur.parentId;
        cur = parentId ? map.get(parentId) : undefined;
    }
    return { kind: 'none' };
}
/** Convenience: returns the def or null (collapses opt-out and none). */
function resolveStreamCustomField(streamTreeOrMap, streamId, eventType) {
    const r = resolveStreamCustomFieldDetailed(streamTreeOrMap, streamId, eventType);
    return r.kind === 'def' ? r.def : null;
}
const EVENT_TYPE_TO_FORM_TYPE = {
    'note/txt': 'text',
    'note/html': 'text',
    'count/generic': 'number',
    'date/iso-8601': 'date',
    'activity/plain': 'text'
};
/**
 * Convenience for the form engine: resolve and convert in one call.
 * Returns null if the field is missing or opted-out.
 *
 * The returned `data` mirrors HDSItemDef.data so it can be passed straight
 * into hds-forms-js's `<HDSFormField itemData={...}>`.
 */
function streamCustomFieldToVirtualItem(streamTreeOrMap, streamId, eventType) {
    const def = resolveStreamCustomField(streamTreeOrMap, streamId, eventType);
    if (!def)
        return null;
    // note/txt with options → render as <select>
    let formType = EVENT_TYPE_TO_FORM_TYPE[eventType];
    let options;
    if (eventType === 'note/txt' && def.options && def.options.length > 0) {
        formType = 'select';
        options = def.options.map((v) => ({ value: v, label: { en: v } }));
    }
    return {
        key: `${def.templateId}::${def.key}`,
        data: {
            type: formType,
            eventType,
            streamId,
            label: def.label,
            description: def.description,
            repeatable: def.repeatable,
            required: def.required,
            maxLength: def.maxLength,
            options,
            min: def.min,
            max: def.max,
            step: def.step,
            minDate: def.minDate,
            maxDate: def.maxDate,
            section: def.section
        },
        customField: def,
        eventTemplate() { return { streamIds: [streamId], type: eventType }; }
    };
}
function isStreamMap(v) {
    return typeof v.get === 'function';
}
/**
 * Convert a `CustomFieldDeclaration` (as carried on a template / CollectorRequest)
 * to a `VirtualItemDef` for form-engine consumption. Use at compose time when
 * the declaration is in hand — no stream-tree lookup needed.
 *
 * Equivalent to `streamCustomFieldToVirtualItem` minus the parent-chain walk.
 */
function customFieldDeclarationToVirtualItem(decl) {
    const def = decl.def;
    let formType = EVENT_TYPE_TO_FORM_TYPE[decl.eventType];
    let options;
    if (decl.eventType === 'note/txt' && def.options && def.options.length > 0) {
        formType = 'select';
        options = def.options.map((v) => ({ value: v, label: { en: v } }));
    }
    return {
        key: `${def.templateId}::${def.key}`,
        data: {
            type: formType,
            eventType: decl.eventType,
            streamId: decl.streamId,
            label: def.label,
            description: def.description,
            repeatable: def.repeatable,
            required: def.required,
            maxLength: def.maxLength,
            options,
            min: def.min,
            max: def.max,
            step: def.step,
            minDate: def.minDate,
            maxDate: def.maxDate,
            section: def.section
        },
        customField: def,
        eventTemplate() { return { streamIds: [decl.streamId], type: decl.eventType }; }
    };
}
